/**
 * Adding, renaming, recolouring and retiring the stock room's categories.
 *
 * These were a fixed list of nine in the source, so a shop could file an
 * ingredient under one and never change the list — while product categories
 * had been editable rows all along. Reached from the Stock room rather than
 * Settings, because that is where you are standing when you notice the
 * grouping is wrong.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import { api } from '../lib/api.js';
import { Button, Input, Modal, Skeleton, cx, useToast } from '../components/ui.js';

export function IngredientCategoriesModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const categories = useQuery({
    queryKey: ['ingredient-categories', 'all'],
    queryFn: () => api.inventory.categories(true),
  });
  const ingredients = useQuery({ queryKey: ['ingredients'], queryFn: api.inventory.ingredients });

  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  /** How many ingredients each category holds, so the cost of removing one is visible. */
  const counts = useMemo(() => {
    const tally = new Map<string, number>();
    for (const ingredient of ingredients.data ?? []) {
      tally.set(ingredient.group, (tally.get(ingredient.group) ?? 0) + 1);
    }
    return tally;
  }, [ingredients.data]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['ingredient-categories'] });
    queryClient.invalidateQueries({ queryKey: ['ingredients'] });
  };

  const create = useMutation({
    mutationFn: () => api.inventory.createCategory({ name: newName.trim() }),
    onSuccess: (created) => {
      setNewName('');
      refresh();
      toast.push({ tone: 'ok', title: `${created.name} added` });
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not add it', body: error.message }),
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.inventory.updateCategory(id, { name }),
    onSuccess: () => {
      setEditingId(null);
      refresh();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not rename it', body: error.message }),
  });

  const recolour = useMutation({
    mutationFn: ({ id, color }: { id: string; color: string }) => api.inventory.updateCategory(id, { color }),
    onSuccess: refresh,
  });

  const setActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.inventory.updateCategory(id, { isActive }),
    onSuccess: refresh,
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not change it', body: error.message }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.inventory.deleteCategory(id),
    onSuccess: (result) => {
      refresh();
      toast.push({
        tone: 'ok',
        title: result.action === 'deactivated' ? 'Category hidden' : 'Category deleted',
        body:
          result.action === 'deactivated'
            ? 'The ones this app ships with are hidden rather than removed, so an ingredient filed under one years ago still shows a name.'
            : undefined,
      });
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not remove it', body: error.message }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Ingredient categories"
      description="How the stock room is grouped. Renaming one keeps everything filed under it."
      footer={
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="space-y-3">
        <div className="flex gap-2">
          <Input
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && newName.trim()) create.mutate();
            }}
            placeholder="New category, e.g. Chocolate"
          />
          <Button
            variant="primary"
            icon={<Plus className="size-4" />}
            loading={create.isPending}
            disabled={!newName.trim()}
            onClick={() => create.mutate()}
          >
            Add
          </Button>
        </div>

        {categories.isLoading ? (
          <Skeleton className="h-48" />
        ) : (
          <ul className="divide-y divide-[var(--color-line)]">
            {categories.data?.map((category) => {
              const used = counts.get(category.key) ?? 0;
              const isEditing = editingId === category.id;

              return (
                <li key={category.id} className="flex items-center gap-2 py-2">
                  <input
                    type="color"
                    value={category.color}
                    onChange={(event) => recolour.mutate({ id: category.id, color: event.target.value })}
                    title={`Colour for ${category.name}`}
                    className="size-6 shrink-0 cursor-pointer rounded-full border border-[var(--color-line-strong)] bg-transparent p-0"
                  />

                  {isEditing ? (
                    <Input
                      autoFocus
                      value={editingName}
                      onChange={(event) => setEditingName(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && editingName.trim()) {
                          rename.mutate({ id: category.id, name: editingName.trim() });
                        }
                        if (event.key === 'Escape') setEditingId(null);
                      }}
                      className="h-9 min-w-0 flex-1"
                    />
                  ) : (
                    <div className="min-w-0 flex-1">
                      <span className={cx('text-sm font-medium', !category.isActive && 'line-through opacity-50')}>
                        {category.name}
                      </span>
                      <span className="ml-2 text-xs text-[var(--color-ink-faint)]">
                        {used === 0 ? 'nothing filed under it' : `${used} ingredient${used === 1 ? '' : 's'}`}
                      </span>
                    </div>
                  )}

                  {isEditing ? (
                    <>
                      <Button
                        size="sm"
                        variant="primary"
                        loading={rename.isPending}
                        disabled={!editingName.trim()}
                        onClick={() => rename.mutate({ id: category.id, name: editingName.trim() })}
                      >
                        Save
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(category.id);
                          setEditingName(category.name);
                        }}
                        title={`Rename ${category.name}`}
                        aria-label={`Rename ${category.name}`}
                        className="rounded-[var(--radius-md)] p-1.5 text-[var(--color-ink-faint)] transition hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink)]"
                      >
                        <Pencil className="size-4" />
                      </button>

                      {/*
                        * Hiding keeps it out of the pickers without touching
                        * what is already filed under it — which is what a shop
                        * usually wants for a category it has stopped using.
                        */}
                      <button
                        type="button"
                        onClick={() => setActive.mutate({ id: category.id, isActive: !category.isActive })}
                        className="rounded-[var(--radius-md)] px-2 py-1 text-xs text-[var(--color-ink-soft)] transition hover:bg-[var(--color-surface-sunken)]"
                      >
                        {category.isActive ? 'Hide' : 'Show'}
                      </button>

                      {/*
                        * Removing is only offered where nothing is filed under
                        * it. The server refuses either way and says how many
                        * would be stranded, but there is no sense in a button
                        * whose only possible outcome is a refusal.
                        */}
                      {used === 0 && (
                        <button
                          type="button"
                          onClick={() => remove.mutate(category.id)}
                          title={category.isProtected ? `Hide ${category.name}` : `Delete ${category.name}`}
                          aria-label={`Delete ${category.name}`}
                          className="rounded-[var(--radius-md)] p-1.5 text-[var(--color-ink-faint)] transition hover:bg-[var(--color-danger-soft)] hover:text-[var(--color-danger)]"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      )}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}
